/**
 * Fetch Google user profile (email + name + picture) for the current
 * OAuth access token.
 *
 * GIS' OAuth Token Model returns ONLY scope+token in the
 * `tokenResponse` — no identifying info (email/name/picture). To
 * populate the SettingsScreen user-card hero we need to call Google's
 * userinfo endpoint with the token in the `Authorization` header.
 *
 * Required scopes (granted via `lib/auth/gis.ts:REQUIRED_SCOPES`):
 *   - `openid` + `email` → returns `email`, `email_verified`
 *   - `profile`          → returns `name`, `picture`
 *
 * Sharp edges:
 *   - Sometimes the token has insufficient scopes (older grants from
 *     before `profile` was added). The endpoint returns 200 with a
 *     subset; we treat missing fields as null rather than throw — the
 *     UI must tolerate `name` being null and fall back to the email's
 *     local-part.
 *   - The endpoint occasionally rate-limits (429) when the same token
 *     is reused across many tabs. Caller should call once per session
 *     and cache, NOT on every render.
 */

export interface GoogleUserinfo {
  /** OAuth `sub` claim — stable user id. */
  sub: string;
  email: string | null;
  emailVerified: boolean;
  name: string | null;
  /** Profile picture URL — large-ish (~96px). May be `null` for users
   *  who never set a Google avatar. */
  picture: string | null;
}

const USERINFO_URL = 'https://www.googleapis.com/oauth2/v3/userinfo';

export class UserinfoFetchError extends Error {
  readonly status: number;
  constructor(status: number, msg: string) {
    super(`userinfo fetch failed (${status}): ${msg}`);
    this.name = 'UserinfoFetchError';
    this.status = status;
  }
}

interface UserinfoApiResponse {
  sub?: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  picture?: string;
}

export async function fetchGoogleUserinfo(token: string): Promise<GoogleUserinfo> {
  const resp = await fetch(USERINFO_URL, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    let body = '';
    try {
      body = await resp.text();
    } catch {
      /* ignore */
    }
    throw new UserinfoFetchError(resp.status, body || resp.statusText);
  }
  const json = (await resp.json()) as UserinfoApiResponse;
  return {
    sub: json.sub ?? '',
    email: json.email ?? null,
    emailVerified: Boolean(json.email_verified),
    name: json.name ?? null,
    picture: json.picture ?? null,
  };
}
